import {
  BatchFactory,
  Batch,
  BatchFactoryError,
  BatchId,
  BatchType,
  BatchCertifications,
  BatchLocation,
  BatchCertificationFlag,
  BatchCounty,
  BatchMount,
  BatchOwner,
  BatchPurpose,
  BatchSituation,
  BatchIntervention,
  BatchSurface,
  BatchEconomicInfo,
  BatchWindow,
  BatchAlreadyWithOfferFlag
} from '@/Domain/Batch'
import { BatchDto } from '@/Application/Batch'
import { Currency } from '@/Domain/Batch/Currency'
import { SpeciesDto } from '@/Application/Batch/Species/SpeciesDto'
import {
  Species,
  SpeciesFootNumber,
  SpeciesVolume,
  SpeciesUnitCost,
  SpeciesTarget,
  SpeciesObservations,
  SpeciesBinomen
} from '@/Domain/Batch/Species'

export class VuexBatchFactory implements BatchFactory<BatchDto> {
  makeBatch (data: BatchDto): Batch {
    try {
      const batchCertification = this.makeBatchCertifications(data)
      const batchLocation = this.makeBatchLocation(data)
      const batchIntervention = this.makeBatchIntervention(data)
      const batchEconomicInfo = this.makeBatchEconomicInfo(data)
      const bathsAlreadyWhithOffer = this.makeBatchAlreadyWithOfferFlag(
        data.alreadyWithOfferFlag
      )
      const species = this.makeBatchSpecies(data.species)
      return new Batch(
        new BatchId(data.batchId, data.recordId),
        data.batchType as BatchType,
        batchCertification,
        batchLocation,
        batchIntervention,
        BatchSurface.fromString(data.surface),
        batchEconomicInfo,
        new BatchWindow(data.window),
        'm3',
        bathsAlreadyWhithOffer,
        species
      )
    } catch (error) {
      throw new BatchFactoryError(error.message)
    }
  }

  private makeBatchCertifications (data: BatchDto): BatchCertifications {
    return new BatchCertifications(
      data.uneCertification ? BatchCertificationFlag.isTrue() : BatchCertificationFlag.isFalse(),
      data.fscCertification ? BatchCertificationFlag.isTrue() : BatchCertificationFlag.isFalse()
    )
  }

  private makeBatchAlreadyWithOfferFlag (value: boolean): BatchAlreadyWithOfferFlag {
    return value ? BatchAlreadyWithOfferFlag.isTrue() : BatchAlreadyWithOfferFlag.isFalse()
  }

  private makeBatchLocation (data: BatchDto): BatchLocation {
    const county = data.county ? new BatchCounty(data.county) : null
    return new BatchLocation(
      new BatchMount(data.mount),
      new BatchOwner(data.owner),
      county
    )
  }

  private makeBatchIntervention (data: BatchDto): BatchIntervention {
    return new BatchIntervention(
      data.purposeCode as BatchPurpose,
      data.situationCode as BatchSituation
    )
  }

  private makeBatchEconomicInfo (data: BatchDto): BatchEconomicInfo {
    const temporaryDeposit = data.temporaryDeposit ? Currency.fromString(data.temporaryDeposit) : undefined
    return new BatchEconomicInfo(
      Currency.fromString(data.appraisal),
      Currency.fromString(data.taxes),
      temporaryDeposit
    )
  }

  private makeBatchSpecies (data: SpeciesDto[]): Species[] {
    const species: Species[] = []
    for (const singleSpecies of data) {
      species.push(this.parseSingleSpecies(singleSpecies))
    }
    return species
  }

  private parseSingleSpecies (data: SpeciesDto): Species {
    const footNumber = data.footNumber !== '-' ? SpeciesFootNumber.fromString(data.footNumber) : null
    const unitCost = data.cost !== '-' ? SpeciesUnitCost.fromString(data.cost) : null
    const sawOne = data.sawOneCost !== '-'
      ? new SpeciesTarget(
        SpeciesUnitCost.fromString(data.sawOneCost),
        SpeciesVolume.fromString(data.sawOneVolume)
      )
      : null
    const sawTwo = data.sawTwoCost !== '-'
      ? new SpeciesTarget(
        SpeciesUnitCost.fromString(data.sawTwoCost),
        SpeciesVolume.fromString(data.sawTwoVolume)
      )
      : null
    const grinded = data.grindedCost !== '-'
      ? new SpeciesTarget(
        SpeciesUnitCost.fromString(data.grindedCost),
        SpeciesVolume.fromString(data.grindedVolume)
      )
      : null
    const observations = data.observations !== '-' ? new SpeciesObservations(data.observations) : null

    return new Species(
      data.internalId,
      new SpeciesBinomen(data.binomen),
      footNumber,
      SpeciesVolume.fromString(data.volume),
      data.morphologyCode,
      unitCost,
      sawOne,
      sawTwo,
      grinded,
      observations
    )
  }
}
